import * as React from 'react'
import { useCallback } from 'react'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { OverflowDetector } from '@sales-center/components'
import { TruncatedText } from '../SmallLocationCard/TruncatedText'
import {
  getLocationCardStyles,
  useData,
} from '../../Location/SmallLocationCard/hooks'
import { LocationLink } from '../LocationInfoTag'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { SxProps, useTheme } from '@mui/material/styles'

export type LargeLocationCardProps = {
  entityId?: number
  entityType?: ENTITY_TYPES
  location: LocationDTO
  billingLocation: boolean
  isSelected: boolean
  header?: React.ReactNode
  footer?: React.ReactNode
  sx?: SxProps
  style?: React.CSSProperties
  hidePopover?: boolean
  onSelected?: (id: string, checked: boolean) => void
}

export const LargeLocationCard: React.FC<LargeLocationCardProps> = (props) => {
  const {
    header,
    footer,
    sx,
    style,
    billingLocation,
    location,
    isSelected,
    onSelected,
  } = props

  const onCheckedUnChecked = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (onSelected && location.externalLinkId !== null)
        onSelected(location.externalLinkId, checked)
    },
    [location, onSelected],
  )

  const properties = useData(props)
  const hasMoreFields = properties.length > 2

  const [open, toggleExpanded] = useToggleBoolean()

  const label = billingLocation ? 'Bill-to' : 'Service Location'

  const verb = open ? 'Less' : 'More'
  const styles = getLocationCardStyles(useTheme())
  return (
    <>
      <Card elevation={2} sx={{ ...styles.longCard, ...sx }} style={style}>
        {header}
        <Box display="flex" alignItems="center">
          <Typography sx={styles.bold}>{label}</Typography>
          <Box display="flex" ml="auto">
            <FormControlLabel
              sx={{
                label: styles.checkboxLabel,
                root: styles.checkbox,
              }}
              label="Add to Record"
              control={
                <Checkbox
                  color="primary"
                  checked={isSelected}
                  onChange={onCheckedUnChecked}
                  data-test-id="addServiceLocationCheckbox"
                />
              }
            />
          </Box>{' '}
        </Box>
        {location.externalLinkId && location.locationCode && (
          <LocationLink
            externalLocationId={location.externalLinkId}
            externalLocationCode={location.locationCode}
            billingLocation={billingLocation}
          />
        )}

        <OverflowDetector open={open} closedHeight={42}>
          {properties.map((p, i) => (
            <TruncatedText key={i} truncated={!open}>
              {p}
            </TruncatedText>
          ))}
        </OverflowDetector>
        {hasMoreFields && (
          <Typography sx={styles.expandLink} onClick={toggleExpanded}>
            View {verb} Information
          </Typography>
        )}
        {footer}
      </Card>
    </>
  )
}
LargeLocationCard.displayName = 'LargeLocationCard'
